(() => {
  'use strict';

  const STATE_NAMES = {
    AC: 'Acre', AL: 'Alagoas', AP: 'Amapá', AM: 'Amazonas', BA: 'Bahia', CE: 'Ceará', DF: 'Distrito Federal',
    ES: 'Espírito Santo', GO: 'Goiás', MA: 'Maranhão', MT: 'Mato Grosso', MS: 'Mato Grosso do Sul', MG: 'Minas Gerais',
    PA: 'Pará', PB: 'Paraíba', PR: 'Paraná', PE: 'Pernambuco', PI: 'Piauí', RJ: 'Rio de Janeiro', RN: 'Rio Grande do Norte',
    RS: 'Rio Grande do Sul', RO: 'Rondônia', RR: 'Roraima', SC: 'Santa Catarina', SP: 'São Paulo', SE: 'Sergipe', TO: 'Tocantins'
  };

  const FILTER_LABELS = {
    todos: 'Todos', SP: 'São Paulo', RJ: 'Rio de Janeiro', MG: 'Minas Gerais', outros: 'Outros estados'
  };

  const normalize = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

  const haversine = (lat1, lon1, lat2, lon2) => {
    const radius = 6371;
    const toRadians = (degrees) => (degrees * Math.PI) / 180;
    const deltaLat = toRadians(lat2 - lat1);
    const deltaLon = toRadians(lon2 - lon1);
    const a = Math.sin(deltaLat / 2) ** 2
      + Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(deltaLon / 2) ** 2;
    return radius * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const formatDistance = (kilometers) => kilometers < 10
    ? `${kilometers.toFixed(1).replace('.', ',')} km`
    : `${Math.round(kilometers)} km`;

  const isCapital = (state, city) => (
    (state === 'SP' && city === 'São Paulo') || (state === 'RJ' && city === 'Rio de Janeiro')
  );

  const makeElement = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  };

  class MushinStoreLocator {
    constructor(root) {
      this.root = root;
      this.locations = Array.isArray(window.MUSHIN_LOCATIONS) ? window.MUSHIN_LOCATIONS : [];
      this.state = {
        query: '',
        activeFilter: 'todos',
        mapState: null,
        userLocation: null,
        focusId: null,
        hoverId: null
      };

      this.search = root.querySelector('[data-search]');
      this.geoButton = root.querySelector('[data-use-location]');
      this.geoNote = root.querySelector('[data-geo-note]');
      this.chips = Array.from(root.querySelectorAll('[data-filter]'));
      this.title = root.querySelector('[data-results-title]');
      this.list = root.querySelector('[data-list]');
      this.content = root.querySelector('[data-content]');
      this.empty = root.querySelector('[data-empty]');
      this.maps = Array.from(root.querySelectorAll('[data-map]'));
      this.mobileMapButton = root.querySelector('[data-open-mobile-map]');
      this.mobileOverlay = root.querySelector('[data-mobile-overlay]');
      this.mobileCount = root.querySelector('[data-mobile-count]');
      this.mapUrl = root.dataset.mapUrl;
      this.mapsReady = false;

      this.onKeydown = this.onKeydown.bind(this);
      this.bindEvents();
      this.render();
      this.initializeMaps();
      root.dataset.mushinInitialized = 'true';
    }

    bindEvents() {
      this.search.addEventListener('input', (event) => {
        this.state.query = event.target.value;
        this.state.focusId = null;
        this.render();
      });

      this.geoButton.addEventListener('click', () => this.useLocation());

      this.chips.forEach((chip) => {
        chip.addEventListener('click', () => {
          this.state.activeFilter = chip.dataset.filter;
          this.state.mapState = ['SP', 'RJ', 'MG'].includes(chip.dataset.filter) ? chip.dataset.filter : null;
          this.state.focusId = null;
          this.render();
        });
      });

      this.root.querySelectorAll('[data-reset-focus]').forEach((button) => {
        button.addEventListener('click', () => {
          this.state.focusId = null;
          this.render();
        });
      });

      this.root.querySelectorAll('[data-map-back]').forEach((button) => {
        button.addEventListener('click', () => {
          this.state.mapState = null;
          this.state.activeFilter = 'todos';
          this.state.focusId = null;
          this.render();
        });
      });

      this.mobileMapButton.addEventListener('click', () => this.openMobileMap());
      this.root.querySelector('[data-close-mobile-map]').addEventListener('click', () => this.closeMobileMap());
      document.addEventListener('keydown', this.onKeydown);
    }

    onKeydown(event) {
      if (event.key === 'Escape' && !this.mobileOverlay.hidden) this.closeMobileMap();
    }

    useLocation() {
      if (!navigator.geolocation) {
        this.setGeoNote('Seu navegador não suporta localização — busque por cidade, bairro ou CEP.');
        return;
      }

      this.geoButton.disabled = true;
      this.geoButton.setAttribute('aria-busy', 'true');
      this.setGeoNote('Buscando sua localização…');

      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.state.userLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          this.geoButton.disabled = false;
          this.geoButton.removeAttribute('aria-busy');
          this.setGeoNote('Resultados ordenados por proximidade.');
          this.render();
        },
        () => {
          this.geoButton.disabled = false;
          this.geoButton.removeAttribute('aria-busy');
          this.setGeoNote('Não conseguimos acessar sua localização — mas você pode buscar normalmente.');
        },
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
      );
    }

    setGeoNote(message) {
      this.geoNote.textContent = message;
      this.geoNote.hidden = !message;
    }

    getFilteredLocations() {
      const query = normalize(this.state.query.trim());
      return this.locations.filter((location) => {
        if (this.state.mapState && location.state !== this.state.mapState) return false;
        if (['SP', 'RJ', 'MG'].includes(this.state.activeFilter) && location.state !== this.state.activeFilter) return false;
        if (this.state.activeFilter === 'outros' && ['SP', 'RJ', 'MG'].includes(location.state)) return false;
        if (!query) return true;

        const searchable = normalize([
          location.name,
          location.address,
          location.neighborhood,
          location.city,
          location.state,
          location.zipCode
        ].join(' '));
        return searchable.includes(query);
      });
    }

    sortFlat(locations) {
      if (this.state.userLocation) {
        return locations
          .map((location) => ({
            location,
            distance: haversine(
              this.state.userLocation.lat,
              this.state.userLocation.lng,
              location.latitude,
              location.longitude
            )
          }))
          .sort((a, b) => a.distance - b.distance);
      }

      return [...locations]
        .sort((a, b) => `${a.city}${a.name}`.localeCompare(`${b.city}${b.name}`, 'pt-BR'))
        .map((location) => ({ location, distance: null }));
    }

    buildGroups(locations) {
      const priority = ['SP', 'RJ', 'MG'];
      const byState = new Map();

      locations.forEach((location) => {
        if (!byState.has(location.state)) byState.set(location.state, []);
        byState.get(location.state).push(location);
      });

      return Array.from(byState.keys())
        .sort((a, b) => {
          const aPriority = priority.indexOf(a);
          const bPriority = priority.indexOf(b);
          if (aPriority !== -1 || bPriority !== -1) {
            return (aPriority === -1 ? 99 : aPriority) - (bPriority === -1 ? 99 : bPriority);
          }
          return (STATE_NAMES[a] || a).localeCompare(STATE_NAMES[b] || b, 'pt-BR');
        })
        .map((state) => {
          const byCity = new Map();
          byState.get(state).forEach((location) => {
            if (!byCity.has(location.city)) byCity.set(location.city, []);
            byCity.get(location.city).push(location);
          });

          const cities = Array.from(byCity.keys())
            .sort((a, b) => {
              const aCapital = isCapital(state, a);
              const bCapital = isCapital(state, b);
              if (aCapital && !bCapital) return -1;
              if (bCapital && !aCapital) return 1;
              return a.localeCompare(b, 'pt-BR');
            })
            .map((city) => ({
              city,
              locations: byCity.get(city).sort((a, b) => (
                `${a.neighborhood}${a.name}`.localeCompare(`${b.neighborhood}${b.name}`, 'pt-BR')
              ))
            }));

          return { state, cities };
        });
    }

    mapsUrl(location) {
      if (typeof window.mushinMapsUrl === 'function') return window.mushinMapsUrl(location);
      const query = `${location.address}, ${location.neighborhood}, ${location.city} - ${location.state}, ${location.zipCode}`;
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
    }

    createLocationCard(location, distance) {
      const card = makeElement('article', 'mushin-locator__location');
      card.tabIndex = 0;
      card.setAttribute('role', 'button');
      card.setAttribute('aria-label', `Destacar ${location.name} no mapa`);
      card.dataset.locationId = location.id;
      if (location.id === this.state.focusId) card.classList.add('is-focused');

      const top = makeElement('div', 'mushin-locator__location-top');
      const nameWrap = makeElement('div', 'mushin-locator__location-name-wrap');
      nameWrap.append(
        makeElement('span', 'mushin-locator__location-dot'),
        makeElement('span', 'mushin-locator__location-name', location.name)
      );
      top.appendChild(nameWrap);
      if (distance !== null && distance !== undefined) {
        top.appendChild(makeElement('span', 'mushin-locator__distance', formatDistance(distance)));
      }

      const place = makeElement('p', 'mushin-locator__location-place', `${location.neighborhood} · ${location.city}, ${location.state}`);
      const address = makeElement('p', 'mushin-locator__location-address', `${location.address} · CEP ${location.zipCode}`);
      const link = makeElement('a', 'mushin-locator__maps-link', 'Ver no mapa ↗');
      link.href = this.mapsUrl(location);
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.addEventListener('click', (event) => event.stopPropagation());

      card.append(top, place, address, link);
      card.addEventListener('click', () => this.selectLocation(location.id));
      card.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.selectLocation(location.id);
        }
      });
      card.addEventListener('mouseenter', () => this.setHover(location.id));
      card.addEventListener('mouseleave', () => this.setHover(null));
      return card;
    }

    renderList(locations) {
      this.list.replaceChildren();
      const flat = Boolean(this.state.query.trim()) || Boolean(this.state.userLocation);

      if (flat) {
        const fragment = document.createDocumentFragment();
        this.sortFlat(locations).forEach(({ location, distance }) => {
          fragment.appendChild(this.createLocationCard(location, distance));
        });
        this.list.appendChild(fragment);
        return;
      }

      this.buildGroups(locations).forEach((group) => {
        const stateGroup = makeElement('section', 'mushin-locator__state-group');
        stateGroup.appendChild(makeElement('h3', 'mushin-locator__state-heading', STATE_NAMES[group.state] || group.state));

        group.cities.forEach(({ city, locations: cityLocations }) => {
          const cityGroup = makeElement('div', 'mushin-locator__city-group');
          const cityLabel = isCapital(group.state, city) ? `${city} — Capital` : city;
          cityGroup.appendChild(makeElement('h4', 'mushin-locator__city-heading', cityLabel));
          cityLocations.forEach((location) => cityGroup.appendChild(this.createLocationCard(location, null)));
          stateGroup.appendChild(cityGroup);
        });

        this.list.appendChild(stateGroup);
      });
    }

    async initializeMaps() {
      try {
        if (!window.MUSHIN_BRAZIL_MAP_PROMISE) {
          window.MUSHIN_BRAZIL_MAP_PROMISE = fetch(this.mapUrl).then((response) => {
            if (!response.ok) throw new Error(`Mapa indisponível (${response.status})`);
            return response.text();
          });
        }

        const svgMarkup = await window.MUSHIN_BRAZIL_MAP_PROMISE;
        const counts = this.locations.reduce((result, location) => {
          result[location.state] = (result[location.state] || 0) + 1;
          return result;
        }, {});

        this.maps.forEach((map) => {
          const host = map.querySelector('[data-svg-host]');
          host.innerHTML = svgMarkup;
          const svg = host.querySelector('svg');
          const title = svg.querySelector('title');
          svg.removeAttribute('id');
          svg.removeAttribute('width');
          svg.removeAttribute('height');
          if (title) {
            title.id = `mushin-brazil-map-title-${this.root.dataset.sectionId}-${this.maps.indexOf(map)}`;
            svg.setAttribute('aria-labelledby', title.id);
          }
          svg.setAttribute('viewBox', '0 0 450 460');
          svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');

          svg.querySelectorAll('.estado').forEach((stateElement) => {
            const abbreviation = stateElement.querySelector('text')?.textContent.trim();
            if (!abbreviation) return;
            stateElement.dataset.state = abbreviation;
            stateElement.removeAttribute('href');
            stateElement.removeAttributeNS('http://www.w3.org/1999/xlink', 'href');
            stateElement.setAttribute('role', 'button');
            stateElement.setAttribute('tabindex', '0');
            stateElement.setAttribute('aria-label', `${STATE_NAMES[abbreviation]} — ${counts[abbreviation] || 0} pontos de venda`);
            stateElement.classList.toggle('has-locations', Boolean(counts[abbreviation]));
            stateElement.addEventListener('click', (event) => {
              event.preventDefault();
              this.selectMapState(abbreviation);
            });
            stateElement.addEventListener('keydown', (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                this.selectMapState(abbreviation);
              }
            });
          });

          const pins = document.createElementNS('http://www.w3.org/2000/svg', 'g');
          pins.setAttribute('data-map-pins', '');
          svg.appendChild(pins);
          map._mushinSvg = svg;
        });

        this.mapsReady = true;
        this.renderMaps(this.getFilteredLocations());
      } catch (error) {
        this.maps.forEach((map) => {
          map.querySelector('[data-svg-host]').textContent = 'Não foi possível carregar o mapa do Brasil.';
        });
      }
    }

    selectMapState(state) {
      this.state.mapState = state;
      this.state.activeFilter = ['SP', 'RJ', 'MG'].includes(state) ? state : 'todos';
      this.state.query = '';
      this.search.value = '';
      this.state.focusId = null;
      this.render();
    }

    projectToBrazil(location) {
      const x = 10 + ((location.longitude + 74) / 40) * 435;
      const y = 8 + ((6 - location.latitude) / 40) * 444;
      return {
        x: Math.max(3, Math.min(447, x)),
        y: Math.max(3, Math.min(457, y))
      };
    }

    stateViewBox(svg, state) {
      if (!state) return { x: 0, y: 0, width: 450, height: 460 };
      const stateElement = svg.querySelector(`[data-state="${state}"]`);
      if (!stateElement) return { x: 0, y: 0, width: 450, height: 460 };
      const bounds = stateElement.getBBox();
      const padding = Math.max(bounds.width, bounds.height) * .2 + 8;
      return {
        x: Math.max(0, bounds.x - padding),
        y: Math.max(0, bounds.y - padding),
        width: Math.min(450, bounds.width + padding * 2),
        height: Math.min(460, bounds.height + padding * 2)
      };
    }

    createSvgPin(location, radius) {
      const point = this.projectToBrazil(location);
      const pin = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      pin.classList.add('mushin-locator__svg-pin');
      pin.dataset.pinId = location.id;
      pin.setAttribute('role', 'button');
      pin.setAttribute('tabindex', '0');
      pin.setAttribute('aria-label', `${location.name}, ${location.city}`);
      pin.setAttribute('transform', `translate(${point.x} ${point.y})`);
      circle.setAttribute('r', radius);
      pin.appendChild(circle);
      if (location.id === this.state.focusId || location.id === this.state.hoverId) pin.classList.add('is-highlighted');
      pin.addEventListener('click', () => this.selectLocation(location.id, false));
      pin.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.selectLocation(location.id, false);
        }
      });
      return pin;
    }

    renderMaps(locations) {
      if (!this.mapsReady) return;
      const focused = this.state.focusId
        ? this.locations.find((location) => location.id === this.state.focusId)
        : null;

      this.maps.forEach((map) => {
        const svg = map._mushinSvg;
        const popup = map.querySelector('[data-popup]');
        const reset = map.querySelector('[data-reset-focus]');
        const back = map.querySelector('[data-map-back]');
        const mapTitle = map.querySelector('[data-map-title]');
        const mapHelp = map.querySelector('[data-map-help]');
        const pins = svg.querySelector('[data-map-pins]');
        const viewBox = this.stateViewBox(svg, this.state.mapState);
        const viewBoxValue = `${viewBox.x} ${viewBox.y} ${viewBox.width} ${viewBox.height}`;
        svg.setAttribute('viewBox', viewBoxValue);
        svg.classList.toggle('is-zoomed', Boolean(this.state.mapState));
        reset.hidden = !focused;
        back.hidden = !this.state.mapState;
        mapTitle.textContent = this.state.mapState ? STATE_NAMES[this.state.mapState] : 'Brasil';
        mapHelp.textContent = this.state.mapState
          ? `${locations.length} ${locations.length === 1 ? 'ponto neste estado' : 'pontos neste estado'}`
          : 'Selecione um estado para aproximar';

        svg.querySelectorAll('.estado').forEach((stateElement) => {
          stateElement.classList.toggle('is-selected', stateElement.dataset.state === this.state.mapState);
          stateElement.classList.toggle('is-muted', Boolean(this.state.mapState) && stateElement.dataset.state !== this.state.mapState);
        });

        pins.replaceChildren();
        const radius = this.state.mapState ? Math.max(viewBox.width, viewBox.height) / 82 : 5;
        locations.forEach((location) => pins.appendChild(this.createSvgPin(location, radius)));

        if (focused) {
          const point = this.projectToBrazil(focused);
          const left = Math.max(14, Math.min(86, ((point.x - viewBox.x) / viewBox.width) * 100));
          const top = Math.max(16, Math.min(88, ((point.y - viewBox.y) / viewBox.height) * 100));
          const name = makeElement('p', 'mushin-locator__popup-name', focused.name);
          const place = makeElement('p', 'mushin-locator__popup-place', `${focused.neighborhood} · ${focused.city}, ${focused.state}`);
          const link = makeElement('a', 'mushin-locator__popup-link', 'Ver no Google Maps ↗');
          link.href = this.mapsUrl(focused);
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          popup.replaceChildren(name, place, link);
          popup.style.left = `${left}%`;
          popup.style.top = `${top}%`;
          popup.hidden = false;
        } else {
          popup.hidden = true;
          popup.replaceChildren();
        }
      });
    }

    setHover(id) {
      this.state.hoverId = id;
      this.root.querySelectorAll('[data-pin-id]').forEach((pin) => {
        pin.classList.toggle('is-highlighted', pin.dataset.pinId === id || pin.dataset.pinId === this.state.focusId);
      });
    }

    selectLocation(id, openMobile = true) {
      const location = this.locations.find((item) => item.id === id);
      this.state.focusId = id;
      if (location) {
        this.state.mapState = location.state;
        this.state.activeFilter = ['SP', 'RJ', 'MG'].includes(location.state) ? location.state : 'todos';
      }
      this.render();
      if (openMobile && window.matchMedia('(max-width: 1023px)').matches) this.openMobileMap();
    }

    openMobileMap() {
      this.mobileOverlay.hidden = false;
      this.mobileMapButton.hidden = true;
      document.body.classList.add('mushin-map-open');
      const closeButton = this.root.querySelector('[data-close-mobile-map]');
      window.requestAnimationFrame(() => closeButton.focus());
    }

    closeMobileMap() {
      this.mobileOverlay.hidden = true;
      this.mobileMapButton.hidden = this.getFilteredLocations().length === 0 && !this.state.mapState;
      document.body.classList.remove('mushin-map-open');
      window.requestAnimationFrame(() => this.mobileMapButton.focus());
    }

    updateHeader(count) {
      const query = this.state.query.trim();
      if (query) {
        this.title.textContent = count
          ? `${count} ${count === 1 ? 'lugar' : 'lugares'} perto de “${query}”`
          : `Nenhum resultado para “${query}”`;
      } else if (this.state.mapState) {
        this.title.textContent = `${count} ${count === 1 ? 'lugar' : 'lugares'} em ${STATE_NAMES[this.state.mapState]}`;
      } else if (this.state.userLocation) {
        this.title.textContent = `${count} ${count === 1 ? 'lugar' : 'lugares'} mais próximos de você`;
      } else if (this.state.activeFilter === 'todos') {
        this.title.textContent = `${count} lugares Mushin por todo o Brasil`;
      } else {
        this.title.textContent = `${count} ${count === 1 ? 'lugar' : 'lugares'} em ${FILTER_LABELS[this.state.activeFilter]}`;
      }
    }

    render() {
      const filtered = this.getFilteredLocations();
      const count = filtered.length;

      this.updateHeader(count);
      this.chips.forEach((chip) => {
        const active = this.state.mapState
          ? chip.dataset.filter === this.state.mapState
          : chip.dataset.filter === this.state.activeFilter;
        chip.classList.toggle('is-active', active);
        chip.setAttribute('aria-pressed', String(active));
      });

      this.content.hidden = false;
      this.mobileMapButton.hidden = (count === 0 && !this.state.mapState) || !this.mobileOverlay.hidden;
      this.mobileCount.textContent = `${count} ${count === 1 ? 'lugar' : 'lugares'}`;

      if (count) {
        this.empty.hidden = true;
        this.renderList(filtered);
        this.renderMaps(filtered);
      } else {
        this.empty.hidden = false;
        this.list.replaceChildren(this.empty);
        this.renderMaps([]);
      }
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-mushin-store-locator]').forEach((root) => {
      if (root.dataset.mushinInitialized !== 'true') new MushinStoreLocator(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
